import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/lib/store';
import AppLayout from '@/components/layout/AppLayout';

// Lazy-style direct imports (pages will be created separately)
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import StudentDashboard from '@/pages/StudentDashboard';
import BrowseStudies from '@/pages/BrowseStudies';
import StudyDetail from '@/pages/StudyDetail';
import MyAppointments from '@/pages/MyAppointments';
import ProfilePage from '@/pages/ProfilePage';
import ResearcherDashboard from '@/pages/ResearcherDashboard';
import MyStudies from '@/pages/MyStudies';
import CreateStudy from '@/pages/CreateStudy';
import StudyManage from '@/pages/StudyManage';
import AmbassadorPortal from '@/pages/AmbassadorPortal';
import NotificationsPage from '@/pages/NotificationsPage';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  return <>{children}</>;
}

function RoleRedirect() {
  const { user } = useAuthStore();

  if (!user) return <Navigate to="/login" replace />;

  if (!user.onboarding_completed) {
    return <Navigate to="/profile" replace />;
  }

  if (user.role === 'researcher') {
    return <Navigate to="/dashboard" replace />;
  }

  return <Navigate to="/dashboard" replace />;
}

export default function App() {
  const { fetchUser, isLoading } = useAuthStore();

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-12 h-12 rounded-full border-[3px] border-gray-200 dark:border-gray-800" />
            <div className="absolute inset-0 w-12 h-12 rounded-full border-[3px] border-transparent border-t-brand-600 animate-spin" />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">Loading APEX...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />

      {/* Protected app shell */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<RoleRedirect />} />

        {/* Shared routes */}
        <Route path="dashboard" element={<DashboardRouter />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="notifications" element={<NotificationsPage />} />

        {/* Student routes */}
        <Route path="studies" element={<BrowseStudies />} />
        <Route path="studies/:id" element={<StudyDetail />} />
        <Route path="appointments" element={<MyAppointments />} />

        {/* Researcher routes */}
        <Route path="my-studies" element={<MyStudies />} />
        <Route path="create-study" element={<CreateStudy />} />
        <Route path="my-studies/:id" element={<StudyManage />} />

        {/* Ambassador routes */}
        <Route path="ambassador" element={<AmbassadorPortal />} />
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/** Routes the /dashboard path to the correct dashboard based on role */
function DashboardRouter() {
  const { user } = useAuthStore();

  if (!user) return <Navigate to="/login" replace />;

  if (user.role === 'researcher') {
    return <ResearcherDashboard />;
  }

  return <StudentDashboard />;
}
